import * as React from 'react';
import PanicError from 'Errors/Panic';

export const VERTICAL = 'vertical';
export const HORIZONTAL = 'horizontal';
const DIRECTIONS = [VERTICAL, HORIZONTAL];

export const useIsOverflow = (direction, ref, { maxSize = undefined } = {}) => {
  const [isOverflow, setIsOverflow] = React.useState(undefined);

  if (!DIRECTIONS.includes(direction)) {
    throw new PanicError('invalid-or-missing-required-parameter', `useIsOverflow direction parameter is invalid. direction is ${direction}. Must be one of ${DIRECTIONS.toString()}`);
  }

  React.useLayoutEffect(() => {
    const { current } = ref;
    if (!current) {
      return undefined; // No clean up function necessary
    }

    const getContentSize = () => {
      switch (direction) {
        case VERTICAL:
          return current.scrollHeight;
        case HORIZONTAL:
          return current.scrollWidth;
        default:
          throw new PanicError('invalid-or-missing-required-parameter', `useIsOverflow direction parameter is invalid. direction is ${direction}. Must be one of ${DIRECTIONS.toString()}`);
      }
    };

    const getContainerSize = () => {
      switch (direction) {
        case VERTICAL:
          return current.clientHeight;
        case HORIZONTAL:
          return current.clientWidth;
        default:
          throw new PanicError('invalid-or-missing-required-parameter', `useIsOverflow direction parameter is invalid. direction is ${direction}. Must be one of ${DIRECTIONS.toString()}`);
      }
    };

    const checkForOverflow = () => {
      const contentSize = getContentSize();
      const containerSize = maxSize ?? getContainerSize();
      const hasOverflow = contentSize > containerSize;

      setIsOverflow(hasOverflow);
    };

    const mutationObserver = new MutationObserver(checkForOverflow);
    mutationObserver.observe(current, {
      attributes: true,
      subtree: true,
      characterData: true,
      childList: true,
    });

    const resizeObserver = new ResizeObserver(checkForOverflow);
    resizeObserver.observe(current);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [maxSize, ref]);

  return isOverflow;
};
